import { UploadCloud, LucideFileText, Trash2 } from "lucide-react";
import { cx } from "shared/utils/classnames";

import {
  useCallback,
  useRef,
  MutableRefObject,
  ChangeEvent,
  Dispatch,
  SetStateAction
} from "react";
import { toast } from "react-toastify";
import { IconButton, IconButtonSize } from "shared/components/IconButton";
import { Button, ButtonSize } from "shared/components/Button";

const Preview = (props: { file: File; onDelete: () => void }) => {
  if (!props) return null;
  const { name } = props.file;
  return (
    <div className="my-4 flex items-center justify-between rounded-lg border border-alpha-100 bg-white p-5">
      <div className="flex items-center gap-3">
        <LucideFileText className="stroke-content-1000" size="24" />
        <div className="w-full">
          <p className="truncate max-w-[400px] text-sm font-medium text-content-1000">
            {name}
          </p>
        </div>
      </div>
      <IconButton
        Icon={Trash2}
        className="h-10 w-10 stroke-danger-500"
        onClick={() => {
          props.onDelete();
        }}
        size={IconButtonSize.SM}
      />
    </div>
  );
};

/**
 *  The `FileWidget` is a widget for rendering file upload fields.
 *  It is typically used with a string property with data-url format.
 */
export const DropZone = ({
  id,
  title,
  description,
  value,
  setValue
}: {
  id: string;
  title?: string;
  description?: string;
  value?: File[];
  setValue: Dispatch<SetStateAction<File[]>>;
}) => {
  const uploader = useRef<HTMLInputElement>();

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0)
        if (Array.from(e.target.files).some((f) => f.size > 10485760))
          toast.error("File size should be less than 10MB");
        else
          setValue((prev) => [
            ...(prev || []),
            ...Array.from(e.target.files || [])
          ]);
      else setValue([]);
    },
    [setValue]
  );

  return (
    <div>
      {value?.length ? (
        <>
          {Array.from(value).map((f, idx) => (
            <Preview
              key={idx + f.name + f.size}
              file={f}
              onDelete={() => {
                if (uploader.current?.value) uploader.current.value = "";

                setValue(Array.from(value).filter((_, i) => i !== idx));
              }}
            />
          ))}
          <Button
            className="w-full"
            onClick={() => uploader.current?.click()}
            size={ButtonSize.SM}
          >
            Upload more
          </Button>
        </>
      ) : null}
      <label
        className={cx(
          "animate-appear relative flex h-[140px] w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-primary-500 bg-primary-20 hover:bg-primary-50",
          value?.length && "hidden"
        )}
        htmlFor={id}
      >
        <div className="flex flex-col items-center justify-center gap-2 pb-6 pt-5">
          <UploadCloud className="stroke-primary-500 stroke-2" size="24" />
          <p className="text-sm font-bold text-primary-500">
            {title || "Upload image or document"}
          </p>
          {description ? (
            <p className="text-xs font-medium text-primary-500">
              {description}
            </p>
          ) : null}
        </div>

        <input
          ref={uploader as MutableRefObject<HTMLInputElement>}
          accept="image/*,.pdf"
          className="absolute left-0 h-[140px] w-full opacity-0"
          id={id}
          multiple={false}
          onChange={handleChange}
          required={false}
          type="file"
        />
      </label>
    </div>
  );
};
